
import React from 'react';
import { EstateAssets, BankAccountAsset } from '../../../types';
import { Landmark, Trash2, Plus } from 'lucide-react';

interface Props {
    data: EstateAssets;
    onChange: (assets: EstateAssets) => void;
}

export const BankAccountsStep: React.FC<Props> = ({ data, onChange }) => {
    const accounts = data.bank_accounts || [];

    const updateAccount = (index: number, changes: Partial<BankAccountAsset>) => {
        const newAccs = [...accounts];
        newAccs[index] = { ...newAccs[index], ...changes };
        onChange({ ...data, bank_accounts: newAccs });
    };

    const addAccount = () => {
        const newAcc: BankAccountAsset = {
            id: `bank_${Date.now()}`,
            label: '',
            ownership: 'unknown',
            estimated_value: null
        };
        onChange({ ...data, bank_accounts: [...accounts, newAcc] });
    };

    const removeAccount = (index: number) => {
        const newAccs = accounts.filter((_, i) => i !== index);
        onChange({ ...data, bank_accounts: newAccs });
    };

    // Auto-add one if empty
    React.useEffect(() => {
        if (accounts.length === 0) {
            addAccount();
        }
    }, []);

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-ink">Bank & Savings Accounts</h2>
                <p className="text-ink3 mt-2">
                    You don't need every account now — just what you remember.
                </p>
            </div>

            <div className="space-y-4">
                {accounts.map((acc, idx) => (
                    <div key={acc.id} className="bg-white p-5 rounded-xl border border-rule relative group">
                        {accounts.length > 1 && (
                            <button
                                onClick={() => removeAccount(idx)}
                                className="absolute top-4 right-4 text-sub hover:text-ink2 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}

                        <div className="flex flex-col gap-4">
                            {/* Name & Ownership Row */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-1">Bank Name (Optional)</label>
                                    <div className="flex items-center gap-2">
                                        <Landmark className="w-5 h-5 text-ink3" />
                                        <input
                                            type="text"
                                            placeholder="e.g. Barclays"
                                            className="w-full border-b border-rule focus:border-accent focus:outline-none py-1 text-ink font-medium"
                                            value={acc.label || ''}
                                            onChange={(e) => updateAccount(idx, { label: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-1">Ownership</label>
                                    <div className="flex gap-2 text-xs">
                                        {['sole', 'joint', 'unknown'].map(opt => (
                                            <label key={opt} className={`
                                                cursor-pointer border rounded px-3 py-1.5 font-medium transition-colors flex-1 text-center capitalize
                                                ${acc.ownership === opt
                                                    ? 'bg-accent-soft text-accent border-accent'
                                                    : 'bg-white text-ink3 border-rule hover:bg-paper'}
                                            `}>
                                                <input
                                                    type="radio" className="sr-only" name={`ownership_${acc.id}`}
                                                    checked={acc.ownership === opt}
                                                    onChange={() => updateAccount(idx, { ownership: opt as any })}
                                                />
                                                {opt === 'unknown' ? 'Not sure' : opt}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Balance */}
                            <div>
                                <label className="block text-xs font-bold text-ink3 uppercase mb-1">Rough Balance (£)</label>
                                <input
                                    type="number"
                                    className="w-full sm:w-1/2 border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-accent focus:outline-none"
                                    placeholder="0.00"
                                    value={acc.estimated_value ?? ''}
                                    onChange={(e) => updateAccount(idx, { estimated_value: e.target.value ? Number(e.target.value) : null })}
                                />
                            </div>

                            {/* Inheritance */}
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-1">Who inherits this?</label>
                                    <p className="text-[11px] text-ink3 mb-1.5 leading-relaxed">
                                        Spouse exemption is applied only to assets marked as passing to a surviving spouse or civil partner.
                                    </p>
                                    <select
                                        className="w-full sm:w-1/2 border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-accent focus:outline-none"
                                        value={acc.beneficiary_type || ''}
                                        onChange={(e) => {
                                            const val = e.target.value as any;
                                            const updates: Partial<BankAccountAsset> = { beneficiary_type: val };
                                            if (val === 'spouse') updates.spouse_percent = 100;
                                            else if (val === 'charity') updates.spouse_percent = 0;
                                            else if (val === 'direct_descendant') updates.spouse_percent = 0;
                                            else if (val === 'other') updates.spouse_percent = 0;
                                            else if (val === 'mixed') updates.spouse_percent = 50;
                                            updateAccount(idx, updates);
                                        }}
                                    >
                                        <option value="">Select...</option>
                                        <option value="spouse">Spouse / Civil Partner</option>
                                        <option value="direct_descendant">Direct Descendants</option>
                                        <option value="charity">Charity</option>
                                        <option value="other">Other Beneficiary</option>
                                        <option value="mixed">Split (Spouse + Others)</option>
                                    </select>
                                    {acc.beneficiary_type === 'direct_descendant' && (
                                        <p className="text-[10px] text-accent mt-1">Direct descendants include children, grandchildren, stepchildren and adopted children.</p>
                                    )}
                                </div>

                                {acc.beneficiary_type === 'mixed' && (
                                    <div className="bg-accent-soft p-3 rounded-lg border border-accent">
                                        <label className="block text-xs font-bold text-accent uppercase mb-1">
                                            % Passing to Spouse
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                className="w-24 border border-accent rounded px-2 py-1 text-sm text-center font-bold text-accent focus:ring-1 focus:ring-accent focus:outline-none"
                                                value={acc.spouse_percent ?? 50}
                                                onChange={(e) => {
                                                    const val = Math.min(100, Math.max(0, Number(e.target.value)));
                                                    updateAccount(idx, { spouse_percent: val });
                                                }}
                                            />
                                            <span className="text-sm font-bold text-accent">%</span>
                                            <p className="text-[10px] text-accent flex-1 leading-tight ml-2">
                                                Remaining <strong>{100 - (acc.spouse_percent ?? 50)}%</strong> passes to others (taxable).
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}

                <button
                    onClick={addAccount}
                    className="w-full py-3 border-2 border-dashed border-rule rounded-lg text-ink3 font-bold hover:border-accent hover:text-accent transition-all flex items-center justify-center gap-2 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Another Account
                </button>
            </div>
        </div>
    );
};
